import PropTypes from "prop-types";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchNoResults from "../SearchNoResults";

export default function StateBlock({
  kind,
  message = null,
  ariaLabel = null,
  onRetry = null,
  retryLabel = "Retry",
  sx = null,
  messageSx = null,
  children = null,
  loadingProps = null,
  translate = true,
}) {
  const { t } = useTranslation();
  // translate={false}: message/retryLabel are already-resolved strings (rewamp copy via useRewampT)
  const text = (value) => (value && translate ? t(value) : value);

  switch (kind) {
    case "loading":
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6, ...sx }}>
          <CircularProgress
            aria-label={ariaLabel ?? message}
            role="status"
            {...loadingProps}
          />
        </Box>
      );
    case "error":
      return (
        <Alert
          severity="error"
          sx={{ borderRadius: 2, ...sx }}
          action={
            onRetry && (
              <Button color="inherit" size="small" onClick={onRetry}>
                {text(retryLabel)}
              </Button>
            )
          }
        >
          {text(message)}
        </Alert>
      );
    case "empty":
      if (children) return children;
      return message ? (
        <SearchNoResults message={message} translate={translate} sx={{ py: 6, ...sx }} />
      ) : null;
    case "invalid":
      return (
        <Typography
          variant="body1"
          sx={{
            color: "text.textGrey",
            py: 4,
            textAlign: "center",
            ...messageSx,
            ...sx,
          }}
        >
          {text(message)}
        </Typography>
      );
    default:
      return null;
  }
}

StateBlock.propTypes = {
  kind: PropTypes.oneOf(["loading", "empty", "error", "invalid"]).isRequired,
  message: PropTypes.string,
  ariaLabel: PropTypes.string,
  onRetry: PropTypes.func,
  retryLabel: PropTypes.string,
  sx: PropTypes.object,
  messageSx: PropTypes.object,
  children: PropTypes.node,
  loadingProps: PropTypes.object,
  /** false = message/retryLabel are pre-resolved strings, not legacy i18n keys. */
  translate: PropTypes.bool,
};
